import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { apiUrl } from '../utils/apiBase';
import RoundDetail from './round-detail';
import { roundLinkShowsScoring } from './round-link';
import ScoringPage from './scoring-page';

interface RoundLinkTarget {
  status: string;
  leagueId: string;
  roundId: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'inactive' }
  | { kind: 'error' }
  | { kind: 'ready'; target: RoundLinkTarget };

// The organiser can roll a live kolejka back to signups, so the status is asked
// again while the page is open — and when a phone brings the tab back to front.
const STATUS_REFRESH_MS = 30_000;

// The admitting variant carries a secret of its own; without it the page follows
// the signup mode, so the share token alone never opens the door.
const RoundLinkPage: React.FC = () => {
  const { token, admitCode } = useParams<{ token: string; admitCode?: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const fetchStatus = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      const response = await fetch(apiUrl(`/api/public/scoring/${token}`));

      if (response.status === 404) {
        setState({ kind: 'inactive' });

        return;
      }

      if (!response.ok) {
        setState((current) => (current.kind === 'ready' ? current : { kind: 'error' }));

        return;
      }

      const body = (await response.json()) as { status: string; round: { id: number; league: { id: number } } };
      setState({
        kind: 'ready',
        target: { status: body.status, leagueId: String(body.round.league.id), roundId: String(body.round.id) },
      });
    } catch {
      setState((current) => (current.kind === 'ready' ? current : { kind: 'error' }));
    }
  }, [token]);

  useEffect(() => {
    void fetchStatus();

    const handle = window.setInterval(() => void fetchStatus(), STATUS_REFRESH_MS);

    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        void fetchStatus();
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(handle);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchStatus]);

  if (state.kind === 'loading') {
    return (
      <div data-testid="round-link-loading" className="scoring-loading">
        Ładowanie…
      </div>
    );
  }

  if (state.kind === 'inactive' || !token) {
    return (
      <div data-testid="round-link-inactive" className="scoring-inactive-page">
        <h1>Link nieaktywny</h1>
        <p>Ten link do kolejki jest nieaktywny — organizator mógł go wymienić.</p>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div data-testid="round-link-error" className="scoring-error">
        <h1>Błąd</h1>
        <p>Nie udało się sprawdzić, na jakim etapie jest kolejka.</p>
        <button type="button" className="btn-secondary" onClick={() => void fetchStatus()}>
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  const { target } = state;

  if (roundLinkShowsScoring(target.status)) {
    return <ScoringPage token={token} />;
  }

  return <RoundDetail leagueId={target.leagueId} roundId={target.roundId} accessCode={admitCode ?? null} />;
};

export default RoundLinkPage;
